<template>
  <div ref="chartRef" :style="{ width: '100%', height: height }"></div>
</template>

<script>
import * as echarts from 'echarts/dist/echarts'

// 抑制 echarts 的已知无害警告/错误
if (!window._echartsCartesianPatched) {
  // 抑制 error 级别
  const _origError = console.error
  console.error = function (...args) {
    const msg = args[0] && typeof args[0] === 'string' ? args[0] : ''
    if (msg.indexOf('cartesian2d cannot be found') >= 0) return
    return _origError.apply(console, args)
  }
  // 抑制 warn 级别（含 alignTicks + cartesian2d）
  const _origWarn = console.warn
  console.warn = function (...args) {
    const msg = args[0] && typeof args[0] === 'string' ? args[0] : ''
    if (msg.indexOf('cartesian2d cannot be found') >= 0) return
    if (msg.indexOf('alignTicks') >= 0) return
    return _origWarn.apply(console, args)
  }
  window._echartsCartesianPatched = true
}

export default {
  name: 'Chart',
  props: {
    option: { type: Object, default: () => ({}) },
    height: { type: String, default: '300px' },
  },
  data() {
    return { chartInstance: null, _resizeObserver: null, _renderId: 0, _lastTheme: '' }
  },
  watch: {
    option: {
      handler() {
        this.scheduleRender()
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this._resizeObserver = new ResizeObserver(() => this.handleResize())
    if (this.$refs.chartRef) this._resizeObserver.observe(this.$refs.chartRef)
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    if (this._resizeObserver) this._resizeObserver.disconnect()
    if (this.chartInstance) { this.chartInstance.dispose(); this.chartInstance = null }
  },
  methods: {
    // 防抖渲染：同一事件循环内多次调用只执行一次
    scheduleRender() {
      this._renderId++
      const id = this._renderId
      this.$nextTick(() => {
        if (id !== this._renderId) return
        this.renderOption()
      })
    },

    renderOption() {
      const el = this.$refs.chartRef
      if (!el) return

      // 检查数据有效性
      if (!this.option || !Array.isArray(this.option.series) || this.option.series.length === 0) return
      const hasData = this.option.series.some(s => s.data != null && (!Array.isArray(s.data) || s.data.length > 0))
      if (!hasData) return

      // ★ 主题切换检测：销毁重建实例，确保 tooltip 事件绑定完整
      const currentTheme = document.body.getAttribute('arco-theme') || 'light'
      if (this._lastTheme && currentTheme !== this._lastTheme && this.chartInstance) {
        this._lastTheme = currentTheme
        this.chartInstance.dispose()
        this.chartInstance = null
        // 延迟一帧让 DOM 稳定后重新初始化
        this.$nextTick(() => setTimeout(() => this.scheduleRender(), 50))
        return
      }
      this._lastTheme = currentTheme

      // 深拷贝，切断 Vue Proxy 链
      let opt
      try { opt = JSON.parse(JSON.stringify(this.option)) } catch (e) { opt = Object.assign({}, this.option) }

      // ★ 强制 tooltip 配置：appendToBody + confine:false + show:true
      const existing = opt.tooltip && typeof opt.tooltip === 'object' ? opt.tooltip : {}
      opt.tooltip = Object.assign({}, existing, {
        show: true,
        appendToBody: true,
        confine: false,
        enterable: false,
        transitionDuration: 0.1,
        hideDelay: 50,
      })

      let _c = this.chartInstance

      // --- 首次初始化 ---
      if (!_c) {
        const r = el.getBoundingClientRect()
        if (!r.width || !r.height) {
          setTimeout(() => this.scheduleRender(), 100)
          return
        }
        _c = echarts.init(el, null, { renderer: 'canvas' })
        this.chartInstance = _c
        _c.setOption(opt, true)
        return
      }

      // --- 后续更新（数据变化，非主题切换）---
      _c.setOption(opt, false)
    },

    handleResize() {
      const c = this.chartInstance
      if (c) c.resize()
    },
  },
}
</script>

<style scoped>
/* 不加任何额外样式，保持纯净 */
</style>
